import ReportIcon from "@mui/icons-material/AssessmentOutlined";
import { ListItemIcon } from "@mui/material";
import ReportListItem from "./ReportListItem";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { ReportTypesEnum } from "types/enums";
import { useEpochAsDateString } from "./useEpochAsDateString";
import {
  AMERICA_NEW_YORK,
  FORMAT_READABLE_DATE_WITH_DAY,
} from "utils/timeUtils";
import { ReportScheduleType } from "./reportPageTypes";
import { getDateStringWithGeAndLt } from "reports/components/Reports/ReportHeader";
import { useFormatZoned } from "utils/customHooks/genericHooks";
import { useReportConfigTimeZone } from "./reportPageUtils";

/**
 * Capitalizes first letter of each word in string
 *
 * @param {string} str
 */
const startCase = (str: string) =>
  str
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const FORMAT_TIME_AM_PM = "h:mm a";

const PerformanceSummaryListItem = ({
  ge,
  lt,
  name,
  portalReportPath,
  reportGroup,
  reportType,
  reportScheduleType,
  customReportTitle,
  customReportSubtitle,
}: {
  ge: number;
  lt: number;
  name: string;
  portalReportPath: string;
  reportGroup?: string;
  reportType?: ReportTypesEnum;
  reportScheduleType: ReportScheduleType;
  customReportTitle?: string;
  customReportSubtitle?: string;
}) => {
  const { translate } = useFlightTranslation();
  const date = useEpochAsDateString(ge, FORMAT_READABLE_DATE_WITH_DAY);

  // e.g. "12:00 AM - 11:59 PM"
  const formatZoned = useFormatZoned();
  const timeRange = `${formatZoned(ge, FORMAT_TIME_AM_PM)} - ${formatZoned(lt, FORMAT_TIME_AM_PM)}`;
  const timeRangeInReportTz = getDateStringWithGeAndLt(
    ge,
    lt,
    FORMAT_TIME_AM_PM
  );

  const reportGroupText = startCase(`${reportGroup ?? ""}`);
  const groupAndName = `${reportGroupText}${
    reportGroupText ? " - " : ""
  }${name}`;

  const reportTitleCustomSchedule =
    // Custom report with shiftly schedule
    reportScheduleType === ReportScheduleType.SHIFTLY
      ? groupAndName
      : // Other custom report
        reportGroupText;

  const reportTitle = customReportTitle
    ? customReportTitle
    : // Regular Shiftly EOS Report
      reportType === ReportTypesEnum.standardEos
      ? `${translate("Performance_Summary")} - ${groupAndName}`
      : reportTitleCustomSchedule;

  const reportConfigTimeZoneConfig = useReportConfigTimeZone();
  const reportConfigTimeZone = reportConfigTimeZoneConfig ?? AMERICA_NEW_YORK;

  /** if timeRange & zonedTimeRange are different, add a suffix e.g. "(00:00 - 00:00 US/Mountain)" */
  const zonedTimeRangeSuffix =
    timeRange !== timeRangeInReportTz && reportConfigTimeZone
      ? ` (${reportConfigTimeZone}: ${timeRangeInReportTz})`
      : "";
  const defaultReportSubtitle = `${date} | ${timeRange}${zonedTimeRangeSuffix}`;

  return (
    <ReportListItem
      reportTitle={reportTitle}
      reportSubtitle={customReportSubtitle ?? defaultReportSubtitle}
      icon={
        <ListItemIcon>
          <ReportIcon fontSize="large" />
        </ListItemIcon>
      }
      portalReportPath={portalReportPath}
    />
  );
};

export default PerformanceSummaryListItem;
